<script setup lang="ts">
    import { computed } from 'vue';
    import { longDate } from '@/formatters.ts';
    import type { CodeSet, Release, ReleaseSpecification } from '@/shared/helpers/api/types/entities.ts';
    import { getCodeSetLink, getLibraryLink, getReleaseLink } from '@/shared/helpers/routing';

    type ReleaseRow = Omit<Release, 'id'> & { id: string };

    const props = withDefaults(defineProps<{
        releases: Release[];
        paginate?: boolean;
        emptyText?: string;
    }>(), {
        paginate: true,
        emptyText: 'No releases found'
    });

    const releasesWithIdAsString = computed<ReleaseRow[]>(() => props.releases.map(r => ({ ...r, id: `${r.id}` })));

    function isComposite(spec: ReleaseSpecification): boolean {
        if (spec.specificationType === 'ChangeDocument' || spec.specificationType === 'CompositeChangeDocument') {
            return spec.isComposite;
        }
        return false;
    }

    function libraryNames(spec: ReleaseSpecification): string[] | null {
        return spec.specificationType === 'PublishFilter' ? spec.libraryNames : null;
    }

    function compositeLibraries(spec: ReleaseSpecification): string[] {
        return spec.specificationType === 'CompositeChangeDocument' ? spec.libraries : [];
    }

    function facilityNames(spec: ReleaseSpecification): string[] | null {
        return spec.specificationType === 'PublishFilter' ? spec.facilityNames : null;
    }

    function getScopes(codeSets: CodeSet[]): string[] {
        if (!codeSets) {
            return [];
        }
        return codeSets.flatMap(cs => cs.scopes).sort((a, b) => a > b ? 1 : -1);
    }
</script>

<template>
    <b-table
        class="mt-2"
        height="70vh"
        :data="releasesWithIdAsString"
        :paginated="paginate"
        :per-page="50"
        sticky-header
        striped
        narrowed
        hoverable>
        <b-table-column
            v-slot="tableProps"
            field="id"
            label="Name"
            sortable
            searchable>
            <router-link
                :to="getReleaseLink(tableProps.row.id)">
                {{ tableProps.row.id }}
            </router-link>
        </b-table-column>
        <b-table-column
            v-slot="tableProps"
            field="state"
            label="State"
            sortable>
            {{ tableProps.row.state }}
        </b-table-column>
        <b-table-column
            v-slot="tableProps"
            field="specification.specificationType"
            label="Type"
            sortable>
            {{ tableProps.row.specification.specificationType }}
        </b-table-column>
        <b-table-column
            v-slot="tableProps"
            field="specification.libraryName"
            label="Library"
            sortable>
            <router-link
                v-if="tableProps.row.specification && tableProps.row.specification.libraryName"
                :to="getLibraryLink(tableProps.row.specification.libraryName)">
                {{ tableProps.row.specification.libraryName }}
            </router-link>
            <div
                v-else-if="tableProps.row.specification && libraryNames(tableProps.row.specification)"
                class="is-flex">
                <div
                    v-for="(lib, idx) in libraryNames(tableProps.row.specification)"
                    :key="`${lib}-${idx}`">
                    <router-link
                        :to="getLibraryLink(lib)">
                        <span v-if="idx !== 0">, </span>
                        {{ lib }}
                    </router-link>
                </div>
            </div>
            <router-link
                v-else-if="isComposite(tableProps.row.specification) && compositeLibraries(tableProps.row.specification).length === 1"
                :to="getLibraryLink(compositeLibraries(tableProps.row.specification)[0])">
                {{ compositeLibraries(tableProps.row.specification)[0] }}
            </router-link>
            <b v-else>{{ isComposite(tableProps.row.specification) ? 'Multiple' : 'None' }}</b>
        </b-table-column>
        <b-table-column
            v-slot="tableProps"
            field="specification.codeSetDescription"
            label="Set"
            sortable>
            <a
                v-if="tableProps.row.specification.codeSetName"
                :href="getCodeSetLink(tableProps.row.specification.codeSetName)">
                {{ tableProps.row.specification.codeSetDescription }}
            </a>
            <p
                v-else-if="tableProps.row.specification && facilityNames(tableProps.row.specification)">
                {{ facilityNames(tableProps.row.specification)?.join(', ') }}
            </p>
            <b v-else>{{ isComposite(tableProps.row.specification) ? 'Multiple' : 'None' }}</b>
        </b-table-column>
        <b-table-column
            v-slot="tableProps"
            label="Scopes"
            sortable>
            <div class="tags">
                <span
                    v-for="scope in getScopes(tableProps.row.codeSets)"
                    :key="scope"
                    class="tag is-small is-success">
                    {{ scope }}
                </span>
            </div>
        </b-table-column>
        <b-table-column
            v-slot="tableProps"
            field="dateCreated"
            label="Created (UTC)"
            sortable>
            {{ longDate(tableProps.row.dateCreated) }}
        </b-table-column>
        <b-table-column
            v-slot="tableProps"
            field="dateUpdated"
            label="Updated (UTC)"
            sortable>
            {{ longDate(tableProps.row.dateUpdated) }}
        </b-table-column>
        <b-table-column
            v-slot="tableProps"
            field="createdBy.name"
            label="Created by"
            sortable>
            <b-tooltip
                v-if="tableProps.row.createdBy.name && tableProps.row.createdBy.email"
                position="is-left"
                :label="tableProps.row.createdBy.email">
                {{ tableProps.row.createdBy.name }}
            </b-tooltip>
            <div v-else>
                {{ tableProps.row.createdBy.name ?? 'Unknown user' }}
            </div>
        </b-table-column>

        <template #empty>
            <section class="section">
                <div class="content has-text-grey has-text-centered">
                    <p> {{ emptyText }}</p>
                </div>
            </section>
        </template>
    </b-table>
</template>
